// src/context/AdminContext.jsx
import React, { createContext, useContext, useCallback, useMemo, useState } from "react";

const AdminContext = createContext(null);

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used inside AdminProvider");
  return ctx;
};

const backendURL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export const AdminProvider = ({ children }) => {
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const [adminLoading, setAdminLoading] = useState(false);

  const [leaderboardUsers, setLeaderboardUsers] = useState([]);
  const [leaderboardError, setLeaderboardError] = useState("");
const [withdraws, setWithdraws] = useState([]);
const [TotalAmtwithdraws, setTotalAmtWithdraws] = useState();

const [totalInvestment, setTotalInvestment] = useState();
const [TotalMANAULInvestmentAMT, setTotalMANUALInvestmentAMT] = useState();

   const [adminUser, setAdminUser] = useState(null);

  const [alert, setAlert] = useState({ isOpen: false, type: '', message: '' });
  const [announcements, setAnnouncements] = useState([]);
const [announcementsLoading, setAnnouncementsLoading] = useState(false);

const [totalEarningsData, setTotalEarningsData] = useState({
  totalEarnings: 0,
  totalUsers: 0,
});

  const showAlert = (type, message) => {
    setAlert({ isOpen: true, type, message });
    setTimeout(() => setAlert({ isOpen: false, type: '', message: '' }), 2000);
  };
  // ✅ fetch all users (supports server-side search)
  const fetchUsers = useCallback(async (search = "") => {
    try {
      setUsersLoading(true);
      setUsersError("");

      const q = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await fetch(`${backendURL}/api/admin/users${q}`, {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to fetch users");
      }

      setUsers(Array.isArray(data.users) ? data.users : []);
      return data.users || [];
    } catch (e) {
      setUsersError(e?.message || "Failed to fetch users");
      setUsers([]);
      return [];
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // ✅ fetch single user details (optional)
  const fetchUserDetails = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      setSelectedLoading(true);

      const res = await fetch(`${backendURL}/api/admin/users/${userId}`, {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to fetch user details");
      }

      setSelectedUser(data.user || null);
      return data.user || null;
    } catch (e) {
      setSelectedUser(null);
      return null;
    } finally {
      setSelectedLoading(false);
    }
  }, []);

  // ✅ LEADERBOARD (renamed to avoid collision with component)
  const fetchLeaderboard = useCallback(async (limit = 50) => {
    try {
      setAdminLoading(true);
      setLeaderboardError("");

      const res = await fetch(`${backendURL}/api/admin/leaders?limit=${limit}`, {
        credentials: "include",
      });
      const data = await res.json();

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to load leaderboard");
      }

      setLeaderboardUsers(Array.isArray(data.users) ? data.users : []);
      return data.users || [];
    } catch (e) {
      setLeaderboardError(e?.message || "Failed to load leaderboard");
      setLeaderboardUsers([]);
      return [];
    } finally {
      setAdminLoading(false);
    }
  }, []);



const fetchWithdrawRequests = useCallback(async () => {
  try {
    setAdminLoading(true);

    const res = await fetch(`${backendURL}/api/admin/fetch-monthly-withdrawals`, { // Route ka naam match karein
      method: "GET",
      credentials: "include",
    });

    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to fetch withdraw requests");
    }

    // Yahan .list ki jagah .data use karein
    setWithdraws(Array.isArray(data.data) ? data.data : []);
    return data.data || [];
  } catch (e) {
    setWithdraws([]);
    showAlert("error", e.message || "Failed to fetch withdraw requests");
    return [];
  } finally {
    setAdminLoading(false);
  }
}, [backendURL, showAlert]); // Dependencies add kar dein


const fetchCalculateWithdrawRequests = useCallback(async () => {
  try {
    setAdminLoading(true);

    const res = await fetch(`${backendURL}/api/admin/fetch-totalamount-withdrawals`, {
      method: "GET",
      credentials: "include",
    });

    const data = await res.json();
    
    // Check if request was successful
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to fetch withdraw amount");
    }

    // Amount set karein (data.totalWithdrawAmount backend se aa raha hai)
    setTotalAmtWithdraws(data.totalWithdrawAmount || 0);
    
    return data.totalWithdrawAmount || 0;
  } catch (e) {
    // Error aane par 0 set karein taake UI mein NaN na aaye
    setTotalAmtWithdraws(0); 
    showAlert("error", e.message || "Failed to fetch withdraw amount");
    return 0;
  } finally {
    setAdminLoading(false);
  }
}, [backendURL, showAlert]);

const createDummyUser = useCallback(async (payload) => {
  try {
    setAdminLoading(true);

    const res = await fetch(`${backendURL}/api/admin/dummyusers/create`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Create user failed");
    }

    showAlert("success", "User created");
    await fetchUsers(""); // refresh users list
    return true;
  } catch (e) {
    showAlert("error", e.message || "Create user failed");
    return false;
  } finally {
    setAdminLoading(false);
  }
}, [fetchUsers]);


  const checkAdminSession = useCallback(async () => {
    try {
      setAdminLoading(true);
      const res = await fetch(`${backendURL}/api/admin/auth/me`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        setAdminUser(data.admin || null);
      } else {
        setAdminUser(null);
      }
    } catch {
      setAdminUser(null);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const loginAdmin = useCallback(async (username, password) => {
    try {
      setAdminLoading(true);
      const res = await fetch(`${backendURL}/api/admin/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Login failed");

      setAdminUser(data.admin || { username });
      showAlert("success", "Admin logged in");
      return true;
    } catch (e) {
      showAlert("error", e.message || "Login failed");
      return false;
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const logoutAdmin = useCallback(async () => {
    try {
      setAdminLoading(true);
      await fetch(`${backendURL}/api/admin/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      setAdminUser(null);
      setAdminLoading(false);
    }
  }, []);

const toggleUserStatus = useCallback(async (userId, currentStatus) => {
  try {
    setAdminLoading(true);
    // Backend endpoint assumes a toggle or specific path
    const res = await fetch(`${backendURL}/api/admin/users/${userId}/toggle-status`, {
      method: "PATCH", // Ya POST jo aapke backend par set ho
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isActive: !currentStatus }),
    });

    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to update status");
    }

    showAlert("success", `User ${!currentStatus ? 'Activated' : 'Suspended'} successfully`);
    await fetchUsers(""); // Refresh the list
    return true;
  } catch (e) {
    showAlert("error", e.message || "Operation failed");
    return false;
  } finally {
    setAdminLoading(false);
  }
}, [fetchUsers]);


const fetchAnnouncements = useCallback(async () => {
  try {
    setAnnouncementsLoading(true);

    const res = await fetch(`${backendURL}/api/admin/`, {
      method: "GET",
      credentials: "include",
    });
    const data = await res.json();

    if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to fetch announcements");

    setAnnouncements(Array.isArray(data.list) ? data.list : []);
    return data.list || [];
  } catch (e) {
    setAnnouncements([]);
    showAlert("error", e?.message || "Failed to fetch announcements");
    return [];
  } finally {
    setAnnouncementsLoading(false);
  }
}, [showAlert]);

const createAnnouncement = useCallback(async (payload) => {
  try {
    setAdminLoading(true);

    const res = await fetch(`${backendURL}/api/admin/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.message || "Create failed");

    showAlert("success", "Announcement added");
    await fetchAnnouncements();
    return true;
  } catch (e) {
    showAlert("error", e?.message || "Create failed");
    return false;
  } finally {
    setAdminLoading(false);
  }
}, [fetchAnnouncements, showAlert]);

const deleteAnnouncement = useCallback(async (id) => {
  if (!id) return false;
  try {
    setAdminLoading(true);

    const res = await fetch(`${backendURL}/api/admin/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.message || "Delete failed");

    showAlert("success", "Announcement deleted");
    await fetchAnnouncements();
    return true;
  } catch (e) {
    showAlert("error", e?.message || "Delete failed");
    return false;
  } finally {
    setAdminLoading(false);
  }
}, [fetchAnnouncements, showAlert]);




const manualActivatePlan = async (payload) => {
  try {
    const res = await fetch(`${backendURL}/api/admin/manual-activate-plan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (data?.success) {
     showAlert("success", "Plan activated manually");
     
      
    }else{

      showAlert( "error", data?.message);
    }


  } catch (e) {
    showAlert({ isOpen: true, type: "error", message: e.message });
    return false;
  }
};



const HandleChangeWallet = async (formData) => {
  try {
    const res = await fetch(`${backendURL}/api/admin/change-wallet-address`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(formData),
    });

    const data = await res.json();
    if (data?.success) {
     showAlert("success", data.message);
     
      
    }else{

      showAlert( "error", data?.message);
    }


  } catch (e) {
    showAlert({ isOpen: true, type: "error", message: e.message });
    return false;
  }
};




// const fetchTotalInvestment = useCallback(async () => {
//   try {
//     const res = await fetch(`${backendURL}/api/admin/get-total-investment-sum`, {
//       method: "GET",
//       credentials: "include",
//     });

//     const data = await res.json();
//     if (!res.ok || !data?.success) throw new Error("Failed to fetch investment sum");

//     setTotalInvestment(data.totalInvestment || 0);
//   } catch (e) {
//     setTotalInvestment(0);
//     console.error(e);
//   }
// }, [backendURL]);


// const fetchTotalManualInvestment = useCallback(async () => {
//   try {
//     const res = await fetch(`${backendURL}/api/admin/manual-plan-investment-sum`, {
//       method: "GET",
//       credentials: "include",
//     });

//     const data = await res.json();
//     if (!res.ok || !data?.success) throw new Error("Failed to fetch investment sum");

//     setTotalMANUALInvestmentAMT(data.totalManualInvestment || 0);
//   } catch (e) {
//     setTotalMANUALInvestmentAMT(0);
//     console.error(e);
//   }
// }, [backendURL]);
const addUserBonus = useCallback(async (email, bonus, password) => {
  try {
    setAdminLoading(true);

    const amount = Number(bonus);

    if (!email?.trim()) {
      showAlert("error", "User email is required");
      return false;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      showAlert("error", "Enter a valid bonus amount");
      return false;
    }

    if (!password) {
      showAlert("error", "Admin password is required");
      return false;
    }

    const res = await fetch(
      `${backendURL}/api/admin/add-user-bonus`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          bonus: amount,
          password,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to add bonus");
    }

    showAlert(
      "success",
      data.message || `$${amount} bonus added successfully`
    );

    // Refresh users list if needed
    await fetchUsers("");

    return true;
  } catch (e) {
    showAlert("error", e?.message || "Failed to add bonus");
    return false;
  } finally {
    setAdminLoading(false);
  }
}, [fetchUsers]);

const fetchTotalEarnings = useCallback(async () => {
  try {

    const res = await fetch(
      `${backendURL}/api/admin/users/total-earnings`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    const data = await res.json();

    if (!res.ok || !data.success) {
      throw new Error(data.message || "Failed to fetch total earnings");
    }

    setTotalEarningsData({
      totalEarnings: Number(data.totalEarnings || 0),
      totalUsers: Number(data.totalUsers || 0),
    });

    return data;
  } catch (error) {
    console.error("Fetch total earnings error:", error);
    return null;
  } 
}, [backendURL]);
  const value = useMemo(
    () => ({
      // users
      users,
      usersLoading,
      usersError,
      fetchUsers,
alert,showAlert,
      // selected
      selectedUser,
      setSelectedUser,
      selectedLoading,
      fetchUserDetails,
fetchWithdrawRequests,withdraws,
      // leaderboard
      adminLoading,
      leaderboardUsers,
      leaderboardError,
      fetchLeaderboard,createDummyUser,adminUser,
checkAdminSession,
loginAdmin,
logoutAdmin,toggleUserStatus,announcements,
announcementsLoading,
fetchAnnouncements,
createAnnouncement,
deleteAnnouncement,manualActivatePlan,TotalAmtwithdraws,HandleChangeWallet,addUserBonus,totalEarningsData,

fetchTotalEarnings,
    }),
    [
      users,
      usersLoading,
      usersError,
      fetchUsers,
      selectedUser,
      selectedLoading,
      fetchUserDetails,
      adminLoading,
      leaderboardUsers,
      leaderboardError,
      fetchLeaderboard,showAlert,alert,
    ]
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};
